import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getLanguage } from "../../../utils/getLanguage";
import Button from "../../Button";
import CustomDataTable from "../../CustomDataTable";
import Modal from "../../Modal";
import ConfirmFinishLessonsModal from "./ConfirmFinishLessonsModal";

const FinishLessonsModal = forwardRef(({ onSubmit }, ref) => {
  const lang = getLanguage();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLessons, setSelectedLessons] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [classroom, setClassroom] = useState(null);
  const [students, setStudents] = useState([]);

  const confirmFinishLessonsModalRef = useRef();

  useImperativeHandle(ref, () => ({
    openModal(classroom, lessons, students) {
      setClassroom(classroom);
      setLessons(lessons.filter((lesson) => !lesson.is_locked && lesson.is_ordered != 0));
      setStudents(students);
      setSelectedLessons([]);
      setIsOpen(true);
    },
    closeModal() {
      handleCloseModal();
    },
  }));

  const handleCloseModal = () => {
    setSelectedLessons([]);
    setLessons([]);
    setClassroom(null);
    setIsOpen(false);
  };

  const handleSelectedRowsChange = (rows) => {
    setSelectedLessons(rows);
  };

  const handleFinishLessons = () => {
    if (selectedLessons.length > 0) {
      confirmFinishLessonsModalRef.current.openModal(classroom, selectedLessons, students);
    }
  };

  const handleConfirmFinishLessonsSuccess = async () => {
    onSubmit(selectedLessons);

    setTimeout(() => {
      handleCloseModal();
    }, 1000);
  };

  const columns = [
    {
      name: (
        <div className="flex items-center space-x-2">
          <span className="text-xl">{t("lesson_name")}</span>
        </div>
      ),
      field: "lesson_title",
      body: (rowData) => (
        <div className="flex items-center space-x-2">
          <span className="block w-80 overflow-hidden truncate whitespace-nowrap text-start font-medium text-blue-500">
            {(lang == "ar" ? rowData.title_ar : rowData.title_en) || rowData.lesson.title}
          </span>
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal
        footer={
          <div className="flex items-center justify-end space-x-2">
            <Button color="white" variant="outlined" onClick={handleCloseModal} label={t("cancel")} />
            <Button
              label={t("finish_lessons")}
              disabled={selectedLessons.length === 0}
              onClick={handleFinishLessons}
              className="w-44"
            />
          </div>
        }
        isOpen={isOpen}
        onClose={handleCloseModal}
        className="w-full max-w-2xl px-0"
        contentClassname="p-0"
        header={<h3 className="text-2xl font-bold text-blue-500">{t("finish_lessons")}</h3>}
      >
        {lessons.length === 0 ? (
          <div className="p-4 text-center text-gray-500">{t("no_lessons_to_finish")}</div>
        ) : (
          <CustomDataTable
            onSelectedRowsChange={handleSelectedRowsChange}
            header={false}
            columns={columns}
            data={lessons}
            withPagination
            withSort
            withSelect
          />
        )}
      </Modal>

      <ConfirmFinishLessonsModal
        ref={confirmFinishLessonsModalRef}
        onSubmit={handleConfirmFinishLessonsSuccess}
      />
    </>
  );
});

export default FinishLessonsModal;
